import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Chip,
  Snackbar,
  Alert as MuiAlert,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationAPI } from '../utils/api';
import type { NotificationData, NotificationResponse } from '../types/notification';
import { buildNotificationImageUrl } from '../utils/notificationImageUrl';
import { useAuth } from '../hooks/useAuth';
import { useListRequest } from '../hooks/useListRequest';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';
import { formatActionTypeLabel } from '../utils/formatActionType';

// Helper function to convert API response to component format
const convertAPINotificationToComponentFormat = (
  notification: NotificationResponse,
  companyId: string,
  timezone: string,
  companyName?: string
): NotificationData => {
  const imageUrl = buildNotificationImageUrl(companyId, notification.image_id, notification.camera_number);
  const status = notification.status || (notification.read_flag === 1 ? 'read' : 'unread');
  
  return {
    id: notification.id,
    employeeName: notification.employee_name,
    employeeId: notification.employee_emp_id,
    department: notification.department_name,
    camera: notification.camera_name,
    imageUrl: imageUrl,
    policyName: notification.policy_name,
    alertLevel: notification.alert_level,
    isRead: notification.read_flag === 1 || status !== 'unread',
    similarity: notification.similarity ? notification.similarity * 100 : undefined,
    detectionTime: formatDateTimeInCompanyTimezone(notification.log_time, timezone || 'UTC'),
    title: notification.title,
    message: notification.message,
    actionType: notification.action_type,
    lastSeenAt: notification.last_seen_at,
    minutesAbsent: notification.minutes_absent,
    createdAt: notification.created_at,
    coordinates: notification.coordinates,
    status,
    acknowledgedAt: notification.acknowledged_at ?? null,
    acknowledgedBy: notification.acknowledged_by ?? null,
    resolvedAt: notification.resolved_at ?? null,
    resolvedBy: notification.resolved_by ?? null,
    resolutionNote: notification.resolution_note ?? null,
    companyId,
    companyName,
  };
};

interface NotificationBellProps {
  onNotificationClick: (notification: NotificationData) => void;
}

const BELL_CACHE_DURATION = 30000;
const bellCache: Record<string, { notifications: NotificationData[]; unreadCount: number; timestamp: number }> = {};

const getBellCacheKey = (companyId: string, userId: number) => `${companyId}_${userId}`;

const NotificationBell: React.FC<NotificationBellProps> = ({ onNotificationClick }) => {
  const { t } = useTranslation('components', { keyPrefix: 'notificationBell' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();
  const { user, currentCompany, companies } = useAuth();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('all');

  const companyId = company_id || currentCompany?.company_id;
  const selectableCompanies = useMemo(
    () => companies.length > 0 ? companies : currentCompany ? [currentCompany] : [],
    [companies, currentCompany]
  );
  const canSelectCompany = selectableCompanies.length > 1;
  const activeCompanyId = canSelectCompany ? selectedCompanyId : companyId;
  const notificationScope = JSON.stringify([user?.id, activeCompanyId, selectableCompanies]);
  const { begin, cancel } = useListRequest(notificationScope);
  
  const CACHE_DURATION = 30000;
  
  const lastFetchTimeRef = useRef<number>(0);
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!activeCompanyId) return;
    setNotifications([]);
    setUnreadCount(0);
    lastFetchTimeRef.current = 0;
    hasFetchedRef.current = false;
    return cancel;
  }, [notificationScope, cancel]);

  useEffect(() => {
    if (!user?.id || !activeCompanyId) return;
    const key = getBellCacheKey(activeCompanyId, user.id);
    const cached = bellCache[key];
    if (cached && Date.now() - cached.timestamp < BELL_CACHE_DURATION) {
      setNotifications(cached.notifications);
      setUnreadCount(cached.unreadCount);
      lastFetchTimeRef.current = cached.timestamp;
      hasFetchedRef.current = true;
    }
  }, [activeCompanyId, user?.id]);

  const fetchNotifications = useCallback(async (forceRefresh = false): Promise<boolean> => {
    if (!user?.id || !activeCompanyId) return false;

    const now = Date.now();
    const isCacheFresh = (now - lastFetchTimeRef.current) < CACHE_DURATION;

    if (!forceRefresh && isCacheFresh && hasFetchedRef.current) {
      console.log('Using cached notifications (fresh data)');
      return true;
    }

    const { signal } = begin();
    if (signal.aborted) return false;
    setLoading(true);
    try {
      const targetCompanies = activeCompanyId === 'all'
        ? selectableCompanies
        : selectableCompanies.filter(company => company.company_id === activeCompanyId);

      if (targetCompanies.length === 0) return true;

      const results = await Promise.allSettled(
        targetCompanies.map(async (company) => {
          const response = await notificationAPI.getNotifications(company.company_id, user.id, {
            unread_only: true,
            per_page: activeCompanyId === 'all' ? 5 : undefined,
          }, signal);

          if (!response.success) {
            return { notifications: [] as NotificationData[], unreadCount: 0, ok: false };
          }

          return {
            notifications: response.data.notifications.map(n =>
              convertAPINotificationToComponentFormat(n, company.company_id, company.timezone || 'UTC', company.name)
            ),
            unreadCount: response.data.unread_count,
            ok: true,
          };
        })
      );
      if (signal.aborted) return false;

      const fulfilledResults = results.filter(
        (result): result is PromiseFulfilledResult<{ notifications: NotificationData[]; unreadCount: number; ok: boolean }> =>
          result.status === 'fulfilled'
      );
      const convertedNotifications = fulfilledResults
        .flatMap(result => result.value.notifications)
        .sort((a, b) => new Date(b.createdAt || b.detectionTime).getTime() - new Date(a.createdAt || a.detectionTime).getTime())
        .slice(0, 5);
      const totalUnread = fulfilledResults.reduce((sum, result) => sum + result.value.unreadCount, 0);

      const ts = Date.now();
      setNotifications(convertedNotifications);
      setUnreadCount(totalUnread);
      lastFetchTimeRef.current = ts;
      hasFetchedRef.current = true;
      if (user?.id) {
        bellCache[getBellCacheKey(activeCompanyId, user.id)] = {
          notifications: convertedNotifications,
          unreadCount: totalUnread,
          timestamp: ts,
        };
      }
      console.log(`Notifications fetched from API (${convertedNotifications.length} unread)`);
      return fulfilledResults.some(result => result.value.ok);
    } catch (error: any) {
      if (signal.aborted) return false;
      console.error('Failed to fetch notifications:', error);

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.warn('Notification fetch timed out - backend may be slow. Try again later.');
      }
      return false;
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [user?.id, activeCompanyId, selectableCompanies, CACHE_DURATION, begin]);
  const latestFetch = useRef(fetchNotifications);
  latestFetch.current = fetchNotifications;

  useEffect(() => {
    if (!user?.id || !activeCompanyId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let consecutiveFailures = 0;

    const scheduleNext = (delay: number) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(tick, delay);
    };

    // Polls every 30s while the tab is visible; hidden tabs poll nothing and resume
    // when the tab becomes visible. Failed polls back off exponentially.
    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        if (!document.hidden) {
          const ok = await latestFetch.current();
          if (cancelled) return;
          consecutiveFailures = ok ? 0 : Math.min(consecutiveFailures + 1, 4);
        }
      } finally {
        inFlight = false;
      }
      if (cancelled) return;
      const delay = consecutiveFailures > 0
        ? Math.min(30000 * 2 ** consecutiveFailures, 240000)
        : 30000;
      scheduleNext(delay);
    };

    const handleVisibilityChange = () => {
      if (cancelled || inFlight) return;
      if (document.visibilityState === 'visible') {
        scheduleNext(0);
      }
    };

    tick();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationScope]);

  useEffect(() => {
    if (Boolean(anchorEl)) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  useEffect(() => {
    const handleForegroundPush = (event: Event) => {
      const payload = (event as CustomEvent<{
        notification?: { title?: string };
        data?: { title?: string };
      }>).detail;
      const title = payload?.notification?.title || payload?.data?.title || t('newNotification', { defaultValue: 'New notification received' });
      setPushMessage(title);
      lastFetchTimeRef.current = 0;
      hasFetchedRef.current = false;
      setUnreadCount(prev => prev + 1);
      fetchNotifications(true);
    };

    window.addEventListener('app:notification-received', handleForegroundPush);
    return () => window.removeEventListener('app:notification-received', handleForegroundPush);
  }, [fetchNotifications]);

  const handleMouseEnter = () => {
    if (!anchorEl) {
      fetchNotifications();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCompanyChange = (event: SelectChangeEvent) => {
    setSelectedCompanyId(event.target.value);
    lastFetchTimeRef.current = 0;
    hasFetchedRef.current = false;
  };

  const handleNotificationClick = async (notification: NotificationData) => {
    const readNotification: NotificationData = {
      ...notification,
      isRead: true,
      status: notification.status === 'unread' ? 'read' : notification.status,
    };

    setNotifications(prev => 
      prev.map(n => n.id === notification.id && n.companyId === notification.companyId ? {
        ...n,
        isRead: true,
        status: n.status === 'unread' ? 'read' : n.status,
      } : n)
    );
    
    if (!notification.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    
    try {
      await notificationAPI.markAsRead(notification.id);
      lastFetchTimeRef.current = 0;
      hasFetchedRef.current = false;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      setNotifications(prev => 
        prev.map(n => n.id === notification.id && n.companyId === notification.companyId ? { ...n, isRead: notification.isRead } : n)
      );
      if (!notification.isRead) {
        setUnreadCount(prev => prev + 1);
      }
    }
    
    handleClose();
    onNotificationClick(readNotification);
  };

  const getAlertColor = (level: string | null) => {
    switch (level) {
      case 'red':
        return 'var(--an-error)';
      case 'yellow':
        return 'var(--an-warning)';
      case 'green':
        return 'var(--an-success)';
      default:
        return 'var(--an-neutral)';
    }
  };

  const formatTimeAgo = (notificationItem: NotificationData): string => {
    const createdAt = new Date(notificationItem.detectionTime).getTime();

    if (isNaN(createdAt)) {
      return '';
    }

    const diffInSeconds = Math.floor((Date.now() - createdAt) / 1000);

    if (diffInSeconds < 60) {
      return t('justNow');
    }

   return notificationItem.detectionTime;
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        sx={{
          width: 38,
          height: 38,
          color: 'var(--an-primary)',
          backgroundColor: 'var(--an-surface-muted)',
          border: '1px solid var(--an-border-light)',
          borderRadius: 'var(--an-radius-sm)',
          boxShadow: 'var(--an-shadow-xs)',
          '&:hover': {
            backgroundColor: 'var(--an-primary-light)',
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

        <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 500,
            mt: 1,
            border: '1px solid var(--an-border-light)',
            borderRadius: 'var(--an-radius-md)',
            boxShadow: 'var(--an-shadow-lg)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('title')}
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('unreadCount', { count: unreadCount })}
            </Typography>
          )}
          {canSelectCompany && (
            <FormControl size="small" fullWidth sx={{ mt: 1.5 }}>
              <InputLabel>{t('companyFilter', { defaultValue: 'Company' })}</InputLabel>
              <Select
                value={selectedCompanyId}
                label={t('companyFilter', { defaultValue: 'Company' })}
                onChange={handleCompanyChange}
              >
                <MenuItem value="all">{t('allCompanies', { defaultValue: 'All companies' })}</MenuItem>
                {selectableCompanies.map(company => (
                  <MenuItem key={company.company_id} value={company.company_id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('noNotifications')}</Typography>
          </Box>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={`${notification.companyId || activeCompanyId}-${notification.id}`}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                py: 2,
                px: 2,
                backgroundColor: notification.isRead ? 'transparent' : 'rgb(var(--an-primary-rgb) / 0.08)',
                '&:hover': {
                  backgroundColor: notification.isRead ? 'var(--an-surface-muted)' : 'rgb(var(--an-primary-rgb) / 0.12)',
                },
                borderBottom: '1px solid var(--an-border-light)',
                display: 'block',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: getAlertColor(notification.alertLevel),
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, wordBreak: 'break-word' }}>
                    {notification.title || `${t('policyAlert')} ${notification.policyName}`}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                    {notification.companyName && canSelectCompany && (
                      <Chip size="small" label={notification.companyName} color="primary" variant="outlined" />
                    )}
                    <Chip size="small" label={formatActionTypeLabel(notification.actionType)} variant="outlined" />
                    {notification.status !== 'unread' && (
                      <Chip size="small" label={t(`status.${notification.status}`, { defaultValue: notification.status })} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                    <strong>{notification.employeeName}</strong> ({notification.employeeId}) {t('detectedAt')}{' '}
                    <strong>{notification.camera}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimeAgo(notification)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </MenuItem>
          ))
        )}

        {notifications.length > 0 && [
          <Divider key="divider" />,
          <MenuItem
            key="view-all"
            onClick={() => {
              handleClose();
              if (activeCompanyId && activeCompanyId !== 'all') {
                navigate(`/company/${activeCompanyId}/notifications`);
              } else if (company_id) {
                navigate(`/company/${company_id}/notifications`);
              } else {
                navigate('/notifications');
              }
            }}
            sx={{
              justifyContent: 'center',
              color: 'primary.main',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'var(--an-primary-light)',
              },
            }}
          >
            {t('viewAll')}
          </MenuItem>
        ]}
      </Menu>
      <Snackbar
        open={Boolean(pushMessage)}
        autoHideDuration={5000}
        onClose={() => setPushMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert severity="info" variant="filled" onClose={() => setPushMessage(null)}>
          {pushMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default NotificationBell;
